import classnames from 'classnames'
import React, { FC, useMemo } from 'react'

import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { PRODUCT_TAGS_IMAGE_LIMIT, TAG_LOCATION, TAG_POSITION } from '@src/quince-site-common/src/utils/constants'
import { getDisplayKey } from '@src/utils/i18n.utils'

import { ProductCardTag } from '../../product-card.types'

import { getTagPriority } from './product-tags.helper'
import styles from './product-tags.module.scss'
import { ReadyToShip } from './ready-to-ship'

interface ProductTagsProps {
  applicableTags?: ProductCardTag[]
  readyToShip?: boolean
  className?: string
  position?: TAG_POSITION
  sticky?: boolean
  rounded?: boolean
  transformToSentenceCase?: boolean
  tagLocation?: TAG_LOCATION
  productTagsNewClass?: boolean
  isModelSizeVisibleOnMobile?: boolean
}

export const ProductTags: FC<ProductTagsProps> = ({
  applicableTags = [],
  readyToShip = false,
  className,
  position = TAG_POSITION.BOTTOM,
  sticky,
  rounded,
  transformToSentenceCase = true,
  tagLocation,
  productTagsNewClass,
  isModelSizeVisibleOnMobile,
}) => {
  const isNewLocalisedFlow = useI18nPreviewContext()
  const { isB2BMode } = useStorefrontModeContext()
  // Sort tags according to priority
  const sortedTags = useMemo(() => {
    return [...applicableTags].sort(
      (a, b) => getTagPriority(a.value.toLowerCase()) - getTagPriority(b.value.toLowerCase()),
    )
  }, [applicableTags])

  const isTagForProduct = tagLocation === TAG_LOCATION.PDP || tagLocation === TAG_LOCATION.QATC
  return (
    <ul
      className={classnames(
        styles.tagList,
        styles[`tagList__${position}`],
        className,
        sticky && styles[`tagList__stickTo__${position}`],
        {
          [`${styles.tagList__buyNow}`]: tagLocation === TAG_LOCATION.QATC,
          [`${styles.tagList__pdp}`]: tagLocation === TAG_LOCATION.PDP,
          [`${styles.tagList__productCard}`]: tagLocation === TAG_LOCATION.PRODUCT_CARD,
          [`${styles.tagList__modelSizeVisibleOnMobile}`]: isModelSizeVisibleOnMobile,
          [`${styles.tagList__modelSizeVisibleOnMobile__newDesignV2}`]:
            isModelSizeVisibleOnMobile && productTagsNewClass,
        },
      )}
    >
      {sortedTags.slice(0, PRODUCT_TAGS_IMAGE_LIMIT).map(tag => {
        const tagText = getDisplayKey(tag, 'displayName', tag.value, isNewLocalisedFlow || isB2BMode)
        return (
          <li
            className={classnames(
              styles.tag,
              tag.tagType && styles[`tag__${tag.tagType}`],
              rounded && styles.tag__rounded,
              isTagForProduct && styles.pdp_tag,
            )}
            key={tag.value}
          >
            {transformToSentenceCase ? productHelper.getTagValueWithFirstLetterUpperCase(tagText) : tagText}
          </li>
        )
      })}
      <ReadyToShip shouldShowReadyToShipTag={readyToShip} />
    </ul>
  )
}
